import {
  Outlet,
  createRootRoute,
  HeadContent,
  Scripts,
  useRouterState,
} from "@tanstack/react-router";
import appCss from "../styles.css?url";
import "@/i18n";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AuthProvider } from "@/admin/AuthContext";
import { QueryClientProvider } from "@tanstack/react-query";
import { getAdminQueryClient } from "@/admin/lib/queryClient";
import { Toaster } from "@/components/ui/sonner";
import { getPublicSiteSettings } from "@/lib/siteSettings.functions";
import { CookieConsent } from "@/components/CookieConsent";
import { GAConsentLoader } from "@/components/GAConsentLoader";
import { DraftPreviewBanner } from "@/components/DraftPreviewBanner";
import { StickerFab } from "@/components/StickerFab";
import { detectLangFromPath } from "@/lib/site";
import { ScrollProgress } from "@/components/ScrollProgress";
import { PageTransition } from "@/components/PageTransition";
import { IntroOverlay } from "@/components/IntroOverlay";
import { FlowingDotsCursor } from "@/components/FlowingDotsCursor";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-7xl font-bold tracking-[-0.04em] text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-medium text-foreground">Page introuvable</h2>
        <p className="mt-2 text-sm text-foreground/65">
          Cette page n'existe pas ou a été déplacée.
        </p>
        <div className="mt-8">
          <a
            href="/"
            className="btn-ink inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-medium"
          >
            Retour à l'accueil
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  loader: async () => getPublicSiteSettings(),
  staleTime: 5 * 60 * 1000,
  head: ({ loaderData }) => {
    const s = loaderData ?? {
      og_image_url: null,
      google_verification: null,
      bing_verification: null,
      ga_id: null,
    };
    const meta: Array<Record<string, string>> = [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#0E1320" },
    ];
    if (s.og_image_url) {
      meta.push({ property: "og:image", content: s.og_image_url });
      meta.push({ name: "twitter:image", content: s.og_image_url });
      meta.push({ name: "twitter:card", content: "summary_large_image" });
    }
    if (s.google_verification) {
      meta.push({ name: "google-site-verification", content: s.google_verification });
    }
    if (s.bing_verification) {
      meta.push({ name: "msvalidate.01", content: s.bing_verification });
    }

    // GA is loaded client-side via <GAConsentLoader /> when analytics consent is granted.
    const scripts: Array<Record<string, string>> = [];

    return {
      meta,
      links: [
        { rel: "icon", type: "image/png", href: "/favicon.png" },
        { rel: "apple-touch-icon", href: "/favicon.png" },
        { rel: "stylesheet", href: appCss },
        { rel: "preconnect", href: "https://fonts.googleapis.com" },
        { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
        {
          rel: "stylesheet",
          href: "https://fonts.googleapis.com/css2?family=Urbanist:wght@400;500;700;800;900&family=Epilogue:wght@400;500;600;700&family=JetBrains+Mono:wght@500;600&display=swap",
        },
      ],
      scripts,
    };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="fr">
      <head>
        <HeadContent />
      </head>
      <body className="bg-background text-foreground">
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isAdmin = pathname === "/admin" || pathname.startsWith("/admin/");
  const loaderData = Route.useLoaderData();
  const gaId = loaderData?.ga_id ?? null;
  const lang = detectLangFromPath(pathname);
  const stickerLabel =
    lang === "en" ? "Contact us" : lang === "nl" ? "Contacteer ons" : "Contactez-nous";
  const hideSticker = pathname.includes("/contact");

  return (
    <QueryClientProvider client={getAdminQueryClient()}>
      <AuthProvider>
        {isAdmin ? (
          // Admin keeps the dark theme look.
          <div className="dark bg-background text-foreground min-h-screen">
            <Outlet />
          </div>
        ) : (
          <div className="relative flex min-h-screen flex-col text-foreground">
            <FlowingDotsCursor />
            <div className="relative z-10 flex min-h-screen flex-col">
              <ScrollProgress />
              <IntroOverlay />
              <DraftPreviewBanner />
              <Header />
              <main className="flex-1">
                <PageTransition>
                  <Outlet />
                </PageTransition>
              </main>
              <Footer />
            </div>
            {!hideSticker && <StickerFab lang={lang} label={stickerLabel} />}
            <CookieConsent />
            <GAConsentLoader gaId={gaId} />
          </div>
        )}
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </QueryClientProvider>
  );
}
